import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Modal, ModalProps, Select } from 'antd'
import { useParams } from 'react-router-dom'

import {
  useIdentityServiceAssignProfileRoleEndpoint,
  useIdentityServiceGetProfileRoleListEndpointKey,
  useUserServiceGetUserRolesListEndpointKey
} from 'src/api/queries'
import { ApiError, LegalEntityStatus, Permissions } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  InternalInstitutionsSelect,
  OtherInstitutionsSelect,
  RoleSelect,
  SaveConfirm,
  Text
} from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  onCancel: () => void
} & ModalProps

enum INSTITUTION_TYPE {
  institution = 'institution',
  ong = 'ong'
}

type FormValues = {
  type?: INSTITUTION_TYPE
  institutionId?: string
  roleId?: string
}

export const AddRoleModal = (props: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()
  const { id } = useParams()

  const { userData, hasPermission } = useUser()

  const isAdmin = hasPermission(Permissions.IDENTITY_MENU_ACCESS_ROLES)

  const [form] = Form.useForm()

  const { mutate, isPending } = useIdentityServiceAssignProfileRoleEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useIdentityServiceGetProfileRoleListEndpointKey]
      })

      queryClient.invalidateQueries({
        queryKey: [useUserServiceGetUserRolesListEndpointKey]
      })

      props.onCancel()
    },
    onError: (e: ApiError) => form.setFields(formErrorHandler(e.body.errors))
  })

  const onFinish = async () => {
    const values = await form.validateFields()

    mutate({
      id: String(id),
      roleId: values.roleId,
      requestBody: {
        legalEntityId: isAdmin
          ? values.institutionId
          : userData?.currentLegalEntityId
      }
    })
  }

  const institutionType = Form.useWatch('type', form)
  const legalEntityId = Form.useWatch('institutionId', form)

  const onValuesChange = (changedValues: Partial<FormValues>) => {
    if (changedValues.type) {
      form.resetFields(['institutionId', 'roleId'])
    }
  }

  return (
    <Modal
      open
      centered
      maskClosable={false}
      title={
        <Text size="xl" weight={700}>
          {t('users.assignee_a_role')}
        </Text>
      }
      footer={
        <Flex justify="center" gap={8}>
          <Button onClick={props.onCancel}>{t('common.cancel')}</Button>

          <SaveConfirm
            title={`${t('common.add')}?`}
            onConfirm={onFinish}
            okButtonProps={{ loading: isPending }}
          >
            <Button type="primary">{t('common.add')}</Button>
          </SaveConfirm>
        </Flex>
      }
      {...props}
    >
      <Form
        layout="vertical"
        requiredMark={false}
        form={form}
        onValuesChange={onValuesChange}
      >
        <Flex vertical gap={16}>
          {isAdmin && (
            <>
              <Form.Item
                label={t('users.select_institution_type')}
                name="type"
                rules={requiredRules}
              >
                <Select
                  placeholder={t('users.select_institution_type')}
                  options={[
                    {
                      label: t('common.institution'),
                      value: INSTITUTION_TYPE.institution
                    },
                    { label: t('common.ong'), value: INSTITUTION_TYPE.ong }
                  ]}
                />
              </Form.Item>
              <Form.Item
                label={t('users.select_institution')}
                name="institutionId"
                rules={requiredRules}
              >
                {institutionType === INSTITUTION_TYPE.institution ? (
                  <InternalInstitutionsSelect
                    disabled={!institutionType}
                    apiStatus={LegalEntityStatus.ACTIVE}
                  />
                ) : (
                  <OtherInstitutionsSelect disabled={!institutionType} />
                )}
              </Form.Item>
            </>
          )}

          <Form.Item
            label={t('common.role')}
            name="roleId"
            rules={requiredRules}
          >
            <RoleSelect
              hasDefault={false}
              legalEntityId={legalEntityId}
              disabled={isAdmin && !legalEntityId}
            />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  )
}
